export type ListFilteredMixpanelPayload = {
  filter_type: string[];
  filter_value: Record<string, string[] | {}>;
};

export type BuildListFilteredPayloadParams = {
  status?: string[];
  source?: string[];
  tags?: string[];
  assignee?: string[];
  dateRangeType?: string;
  dateRange?: { startDate: string; endDate: string };
};

type DateEntry = {
  dateRangeType?: string;
  dateRange?: { startDate?: string; endDate?: string };
};

/**
 * Build Mixpanel payload for lead list filter events.
 * Used both when applying filters (from UI values) and when reading current state (e.g. Redux).
 */
export function buildListFilteredMixpanelPayload(
  params: BuildListFilteredPayloadParams
): ListFilteredMixpanelPayload {
  const filterTypes: string[] = [];
  const filterValue: ListFilteredMixpanelPayload['filter_value'] = {};

  if (params.status?.length) {
    filterTypes.push('status');
    filterValue.status = params.status;
  }
  if (params.source?.length) {
    filterTypes.push('source');
    filterValue.source = params.source;
  }
  if (params.tags?.length) {
    filterTypes.push('tags');
    filterValue.tags = params.tags;
  }
  if (params.assignee?.length) {
    filterTypes.push('assignee');
    filterValue.assignee = params.assignee;
  }
  const dateEntry: DateEntry = {};

  if (params.dateRangeType) dateEntry.dateRangeType = params.dateRangeType;
  if (params.dateRange?.startDate || params.dateRange?.endDate) {
    dateEntry.dateRange = {
      ...(params.dateRange?.startDate && {
        startDate: params.dateRange.startDate
      }),
      ...(params.dateRange?.endDate && { endDate: params.dateRange.endDate })
    };
  }

  if (Object.keys(dateEntry).length) {
    filterTypes.push('lead_creation_date');
    filterValue.lead_creation_date = [dateEntry];
  }
  return { filter_type: filterTypes, filter_value: filterValue };
}

export function computeObjectDifference<T extends Record<string, any>>(
  original: T,
  updated: Partial<T>
): Record<string, any> & { fieldsUpdated: string[] } {
  const valuesAreEqual = (prev: unknown, next: unknown): boolean => {
    if (prev === next) return true;
    if (prev == null || next == null) return false;

    if (Array.isArray(prev) && Array.isArray(next)) {
      if (prev.length !== next.length) return false;
      const allHaveIds = [...prev, ...next].every((item) => item?.id != null);
      if (allHaveIds) {
        const nextIds = new Set(next.map((item) => item.id));
        return prev.every((item) => nextIds.has(item.id));
      }
      return JSON.stringify(prev) === JSON.stringify(next);
    }

    if (typeof prev === 'object' && typeof next === 'object') {
      const [o, n] = [prev, next] as Record<string, unknown>[];
      return 'id' in o && 'id' in n
        ? o.id === n.id
        : JSON.stringify(o) === JSON.stringify(n);
    }

    return false;
  };

  const fieldsUpdated: string[] = [];

  const diff = Object.keys(updated).reduce<Record<string, any>>((acc, key) => {
    if (!valuesAreEqual(original[key], updated[key])) {
      fieldsUpdated.push(key);
      acc[`${key}_changed`] = {
        previous_value: original[key],
        updated_value: updated[key]
      };
    }
    return acc;
  }, {});

  return { ...diff, fieldsUpdated };
}
